import clsx from 'clsx';
import {
  type ForwardedRef,
  type ReactElement,
  type RefAttributes,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import {
  ROW_HEIGHT,
  ROW_KEY,
  SERIES_ID_KEY,
  SERIES_NAME_KEY,
  TOP_LIST_ARIA_LABEL,
  X_AXIS_ID,
  Y_AXIS_ID,
} from './constants.js';
import { scrollContainer } from './TopList.sty.js';
import type { TopListActionItem, TopListProps } from './types/top-list.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { downloadCSV, escapeForCSV } from '../../core/utils/download.js';
import { CSS_DOWNLOAD_FILENAME } from '../core/constants.js';
import { ColorRule } from '../core/slots/ColorRule.js';
import type {
  ChartColorPalette,
  ChartDatapoint,
} from '../core/types/chart-data.js';
import { isColoredRangeArray } from '../core/types/colored-range.js';
import type { CategoricalFormatter } from '../core/types/formatter.js';
import {
  coerceSizeValue,
  coerceSizeValueToPixels,
} from '../core/utils/coerceSizeValue.js';
import { getColorPalette } from '../core/utils/get-color-palette.js';
import { getFormatter } from '../core/utils/get-formatter.js';
import {
  DEFAULT_CHART_HEIGHT,
  DEFAULT_COLOR_PALETTE,
} from '../xy-chart/constants/defaults.js';
import type {
  XYChartBarSeriesActionItem,
  XYChartRef,
} from '../xy-chart/types/xy-chart.js';
import { XYChart } from '../xy-chart/XYChartRoot.js';

export type { ChartColorPalette, TopListActionItem, TopListProps };

const COMPONENT_NAME_BEHAVIOURAL_TRACKING = 'TopList';

function TopListRender<Data extends ChartDatapoint = ChartDatapoint>(
  props: TopListProps<Data>,
  forwardedRef: ForwardedRef<XYChartRef>,
) {
  const {
    data,
    labelAccessor,
    valueAccessor,
    valueFormatter,
    comparator,
    actions,
    colorPaletteMode,
    colorPalette,
    children,
    height = DEFAULT_CHART_HEIGHT,
    width,
    loading,
    truncationMode,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    // @ts-expect-error - We want to allow overriding data-dt-component as we use the component internally in other Strato components, but it should not appear in the public API.
    'data-dt-component': componentName = COMPONENT_NAME_BEHAVIOURAL_TRACKING,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
  } = props;

  const [behaviorTrackingProps] = useBehavioralTrackingProps(props);
  const internalRef = useRef<XYChartRef>(null);
  const mergedRef = useMergeRefs([internalRef, forwardedRef]);
  const onScroll = useCallback(() => internalRef.current?.reset(), []);

  const dataWithSyntheticFields = useMemo(() => {
    const sortDescending = (a: ChartDatapoint, b: ChartDatapoint) =>
      Number(b[valueAccessor]) - Number(a[valueAccessor]);

    return data
      .map((d, i) => ({
        ...d,
        [SERIES_ID_KEY]: 'series',
        [SERIES_NAME_KEY]: 'Series',
        [ROW_KEY]: String(i),
      }))
      .sort(comparator ?? sortDescending) as ChartDatapoint[];
  }, [comparator, data, valueAccessor]);

  const wrappedActions = useMemo(() => {
    if (!actions) {
      return undefined;
    }

    return (item: XYChartBarSeriesActionItem) => {
      const rowKey = item.datapoint[ROW_KEY] as string;
      return actions(data[Number(rowKey)]);
    };
  }, [actions, data]);

  const yAxisFormatter = useMemo<CategoricalFormatter>(() => {
    const numFormatter = getFormatter(valueFormatter);
    return (_rowKey, record) => numFormatter(Number(record[valueAccessor]));
  }, [valueFormatter, valueAccessor]);

  const downloadDataCallback = useCallback(() => {
    const rows = dataWithSyntheticFields
      .map((d) => {
        const label = escapeForCSV(d[labelAccessor]);
        const value = escapeForCSV(d[valueAccessor]);
        return `${label},${value}`;
      })
      .join('\n');
    const csvContent = rows ? `Label,Value\n${rows}\n` : 'Label,Value\n';
    downloadCSV(csvContent, CSS_DOWNLOAD_FILENAME);
  }, [dataWithSyntheticFields, labelAccessor, valueAccessor]);

  const syntheticColorRules = useMemo(() => {
    if (colorPaletteMode !== 'multi-color') {
      return [];
    }

    const effectivePalette = colorPalette ?? DEFAULT_COLOR_PALETTE;

    if (isColoredRangeArray(effectivePalette)) {
      return [];
    }

    return dataWithSyntheticFields.map((item, i) => ({
      valueAccessor: ROW_KEY,
      comparator: 'equals' as const,
      matchValue: String(item[ROW_KEY]),
      color: getColorPalette(String(item[labelAccessor]), i, effectivePalette)
        .Default,
    }));
  }, [colorPaletteMode, colorPalette, dataWithSyntheticFields, labelAccessor]);

  const viewportPx = coerceSizeValueToPixels(height) ?? DEFAULT_CHART_HEIGHT;
  const contentHeight = Math.max(viewportPx, data.length * ROW_HEIGHT);

  return (
    <div
      className={clsx(scrollContainer, className)}
      style={{ ...style, height: coerceSizeValue(height) }}
      onScroll={onScroll}
      data-dt-component={componentName}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...behaviorTrackingProps}
    >
      <XYChart
        data={dataWithSyntheticFields}
        height={contentHeight}
        width={width}
        loading={loading}
        truncationMode={truncationMode}
        colorPalette={colorPalette}
        aria-label={ariaLabel ?? TOP_LIST_ARIA_LABEL}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
        ref={mergedRef}
        __downloadDataCallback={downloadDataCallback}
      >
        <XYChart.BarSeries
          variant="single"
          y0Accessor={ROW_KEY}
          x1Accessor={valueAccessor}
          labelAccessor={labelAccessor}
          seriesIdAccessor={SERIES_ID_KEY}
          nameAccessor={SERIES_NAME_KEY}
          yAxisId={Y_AXIS_ID}
          xAxisId={X_AXIS_ID}
          actions={wrappedActions}
        />
        <XYChart.XAxis
          id={X_AXIS_ID}
          type="numerical"
          position="bottom"
          hidden
          hideCrosshair
          disableZoom
          disablePan
          formatter={valueFormatter}
        />
        <XYChart.YAxis
          id={Y_AXIS_ID}
          type="categorical"
          position="left"
          formatter={yAxisFormatter}
          hideCrosshair
        />
        <XYChart.Legend hidden />
        {syntheticColorRules.map((rule) => (
          <XYChart.ColorRule key={rule.matchValue} {...rule} />
        ))}
        {children}
        <XYChart.Select disabled />
      </XYChart>
    </div>
  );
}

const TopListRoot = forwardRef(TopListRender) as unknown as <
  Data extends ChartDatapoint = ChartDatapoint,
>(
  props: TopListProps<Data> & RefAttributes<XYChartRef>,
) => ReactElement | null;

/**
 * A horizontal ranked-bar chart with labels inside bars and a hidden numerical
 * axis, implemented as a wrapper around XYChart. When the list has more items
 * than fit in `height`, the viewport becomes scrollable.
 *
 * Slots such as `<TopList.ColorRule>` can be passed as children to customize
 * bar colors per datapoint; they are forwarded to the internal `XYChart`.
 * @public
 */
export const TopList = Object.assign(TopListRoot, {
  /**
   * Apply a conditional color override to bars matching the rule. Same
   * component reference as `XYChart.ColorRule` and `TimeseriesChart.ColorRule`.
   */
  ColorRule,
});

(TopList as typeof TopList & { displayName: string }).displayName = 'TopList';
